import LegalLayout, { LegalSection, SUPPORT_EMAIL } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="September 6, 2026">
      <p>
        This Privacy Policy explains how Work &amp; Keep (&ldquo;Work &amp; Keep,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;),
        operated by Wholehearted Wellness LLC, handles information when you use our website and software service (the
        &ldquo;Service&rdquo;). By using the Service you agree to the practices described here.
      </p>

      <LegalSection heading="Information we collect">
        <p>We collect the following, and only what we need to provide the Service:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Account information.</strong> When you create an account we use Firebase Authentication (a Google
            service). Depending on how you sign in, this includes your name, email address, and an authentication
            identifier. We use this to sign you in, secure your account, and send account emails such as verification
            and password reset.
          </li>
          <li>
            <strong>Business information you enter and upload.</strong> The Service is a workspace, so it stores the
            content you choose to add, such as your business setup, brand details, content, ideas, standard operating
            procedures, projects, planning, business figures you enter, saved AI Business Guide conversations, and files
            you upload to your Knowledge Library, Brand Center, or Projects.
          </li>
          <li>
            <strong>Payment information.</strong> Payments are processed by Stripe. We do not collect or store your full
            card number. We store limited billing details returned by Stripe, such as a Stripe customer identifier and
            your subscription status, so we can manage access to your workspace.
          </li>
          <li>
            <strong>Usage and device data.</strong> We use PostHog for product analytics and session recording, which
            captures how the Service is used (for example, pages viewed and interactions) to help us understand and
            improve the product. We also process standard technical data such as log and device information.
          </li>
          <li>
            <strong>Email account access (only if you connect an inbox).</strong> The Service includes an optional
            Email feature. If you choose to connect an email account, then with your explicit authorization we access
            messages you retrieve, including the sender and recipients, subject, date, message body, and attachment
            details, so you can turn them into CRM records, tasks, and follow-ups. You control which messages are
            retrieved, and you can disconnect the account at any time, which deletes the stored connection credentials
            and any synced email data. Your email connection credentials and any access tokens are encrypted at rest.
            The Email feature is read-only in this phase; the Service does not send email on your behalf.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="How we use information">
        <ul className="list-disc pl-6 space-y-2">
          <li>To provide, operate, secure, and maintain the Service and your private workspace.</li>
          <li>To authenticate you and manage your account.</li>
          <li>To process your subscription and setup fee and manage billing.</li>
          <li>To generate responses from the AI Business Guide based on the business context you provide.</li>
          <li>To retrieve and display email from an inbox you connect, and to create or link CRM records, tasks, and follow-ups at your direction. We do not use your email content for advertising and do not use it to train AI or foundation models.</li>
          <li>To understand product usage and improve the Service.</li>
          <li>To communicate with you about your account, support requests, and important changes.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="How the AI Business Guide uses your information">
        <p>
          When you use the AI Business Guide, the business context and prompts you submit are sent to our AI model
          provider to generate a response. The Guide is designed to work from the information in your own workspace. We
          do not use your private workspace content to train third-party foundation models.
        </p>
      </LegalSection>

      <LegalSection heading="Service providers we share information with">
        <p>
          We do not sell your personal information. We share information only with service providers that help us run
          the Service, and only as needed for them to perform their function:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Firebase / Google</strong> — authentication and account emails.</li>
          <li><strong>Stripe</strong> — payment processing and subscription management.</li>
          <li><strong>Our AI model provider</strong> — processing the prompts and context you submit to the AI Business Guide.</li>
          <li><strong>Your email provider</strong> — if you connect an inbox, we connect to the email host you specify (for example, an IMAP/SMTP provider such as Namecheap Private Email) using the credentials you supply, solely to retrieve the messages you choose. Additional email providers may be supported over time.</li>
          <li><strong>PostHog</strong> — product analytics and session recording.</li>
          <li><strong>Cloudflare</strong> — content delivery and security for our website.</li>
          <li><strong>Our hosting, database, and file-storage providers</strong> — storing and serving your workspace data and uploaded files.</li>
        </ul>
        <p>
          These providers process information on our behalf under their own terms and privacy practices. We may also
          disclose information if required by law or to protect our rights, users, or the Service.
        </p>
      </LegalSection>

      <LegalSection heading="Cookies and tracking">
        <p>
          We use a secure session cookie to keep you signed in. Our analytics provider (PostHog) may also use cookies or
          similar technologies to measure usage and record sessions as described above. Our website is served through
          Cloudflare, which may set cookies necessary for security and delivery.
        </p>
      </LegalSection>

      <LegalSection heading="Data retention">
        <p>
          We keep your account and workspace information for as long as your account is active and as needed to provide
          the Service. You can export your data from within your workspace at any time. If you would like your data
          deleted, contact us at {" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary underline">{SUPPORT_EMAIL}</a>, and we will delete
          it, subject to any records we are required to retain by law (for example, transaction records).
        </p>
      </LegalSection>

      <LegalSection heading="Your choices and rights">
        <ul className="list-disc pl-6 space-y-2">
          <li>Access and update much of your information directly within your workspace.</li>
          <li>Export your data from your workspace at any time.</li>
          <li>Request correction or deletion of your information by contacting us.</li>
          <li>Manage your subscription and payment details through the Stripe customer portal.</li>
          <li>Disconnect a connected email account at any time, which removes its stored credentials and synced email data.</li>
        </ul>
        <p>Depending on where you live, you may have additional rights under applicable privacy laws.</p>
      </LegalSection>

      <LegalSection heading="Security">
        <p>
          We take reasonable measures to protect your information, including per-account data isolation and secure
          authentication. Credentials and access tokens for any email account you connect are encrypted at rest. No
          method of transmission or storage is completely secure, so we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection heading="Children">
        <p>The Service is intended for adults and is not directed to children. Please do not use the Service if you are under 18.</p>
      </LegalSection>

      <LegalSection heading="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. When we do, we will update the &ldquo;Last updated&rdquo;
          date above. Your continued use of the Service after changes take effect means you accept the updated policy.
        </p>
      </LegalSection>

      <LegalSection heading="Contact us">
        <p>
          Questions about this Privacy Policy or your data? Contact us at {" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary underline">{SUPPORT_EMAIL}</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
